import React, { useEffect, useState, useRef, useCallback } from 'react';
import { useParams, useNavigate, useLocation } from 'react-router-dom';
import { doc, onSnapshot, collection, query, where, getDocs } from 'firebase/firestore';
import { db, getOrderInvoicePdf } from '../services/firebase';
import { useOrder } from '../context/OrderContext';
import { useLanguage } from '../context/LanguageContext';
import { useBrand } from '../context/BrandContext';

interface OrderData {
  status: string;
  order_number: string;
  total: number;
  subtotal: number;
  tax: number;
  items: Array<{
    name: string;
    name_de?: string;
    quantity: number;
    price: number;
    total: number;
    addons?: Array<{ name: string; price: number }>;
    notes?: string;
  }>;
  order_type: string;
  payment_method: string;
  customer_name: string;
  delivery_address?: string;
  table_number?: number;
  created_at: any;
  delivery_pin?: string;
  delivery_pin_verified?: boolean;
  sealed_by?: string;
  sealed_at?: any;
  delivery_fee?: number;
  discount_amount?: number;
  discount_type?: 'percentage' | 'fixed' | string;
  discount_value?: number;
  discount_reason?: string;
  customer_email?: string;
  tax_breakdown?: { by_rate?: Record<string, { gross?: number; net?: number; tax?: number }> };
  total_before_discount?: number;
}

const STATUS_STEPS_DELIVERY = ['confirmed', 'preparing', 'ready', 'picked_up', 'delivered'];
const STATUS_STEPS_DEFAULT = ['confirmed', 'preparing', 'ready', 'completed'];

// Maps any status variant to the canonical step in the progress bar
// Handles: pending, confirmed, preparing, ready, assigned, picked_up, on_the_way, delivered, completed
function getStepIndex(status: string, steps: string[]): number {
  const s = (status || '').toLowerCase();
  // Direct match first
  const direct = steps.indexOf(s);
  if (direct >= 0) return direct;
  // Map non-step statuses to the right step
  if (s === 'pending') return 0; // pending → confirmed
  if (s === 'assigned') return Math.min(2, steps.length - 1); // assigned → ready (driver picking up next)
  if (s === 'on_the_way' || s === 'in_transit' || s === 'on_delivery') return Math.min(3, steps.length - 1); // → picked_up
  if (s === 'completed' || s === 'delivered') return steps.length - 1; // completed/delivered → final step
  if (s === 'cancelled') return -1;
  return 0;
}

const STATUS_LABELS: Record<string, Record<string, string>> = {
  de: {
    confirmed: 'Bestellt',
    preparing: 'Zubereitung',
    ready: 'Bereit',
    picked_up: 'Abgeholt',
    on_the_way: 'Unterwegs',
    completed: 'Fertig',
    delivered: 'Geliefert',
    cancelled: 'Storniert',
  },
  en: {
    confirmed: 'Confirmed',
    preparing: 'Preparing',
    ready: 'Ready',
    picked_up: 'Picked Up',
    on_the_way: 'On the Way',
    completed: 'Completed',
    delivered: 'Delivered',
    cancelled: 'Cancelled',
  },
};

const STATUS_MESSAGES: Record<string, Record<string, string>> = {
  de: {
    pending: 'Ihre Bestellung wartet auf Bestätigung.',
    confirmed: 'Ihre Bestellung wurde empfangen und wird bearbeitet.',
    preparing: 'Ihre Bestellung wird gerade in der Kuche zubereitet.',
    ready: 'Ihre Bestellung ist fertig!',
    assigned: 'Ein Fahrer wurde Ihrer Bestellung zugewiesen.',
    picked_up: 'Ihr Fahrer hat die Bestellung abgeholt und ist auf dem Weg.',
    on_the_way: 'Ihr Fahrer ist unterwegs zu Ihnen.',
    in_transit: 'Ihr Fahrer ist unterwegs zu Ihnen.',
    on_delivery: 'Ihr Fahrer ist unterwegs zu Ihnen.',
    completed: 'Ihre Bestellung ist abgeschlossen. Vielen Dank!',
    delivered: 'Ihre Bestellung wurde geliefert. Guten Appetit!',
    cancelled: 'Ihre Bestellung wurde storniert.',
  },
  en: {
    pending: 'Your order is waiting for confirmation.',
    confirmed: 'Your order has been received and is being processed.',
    preparing: 'Your order is being prepared in the kitchen.',
    ready: 'Your order is ready!',
    assigned: 'A driver has been assigned to your order.',
    picked_up: 'Your driver has picked up your order and is on the way.',
    on_the_way: 'Your driver is on the way to you.',
    in_transit: 'Your driver is on the way to you.',
    on_delivery: 'Your driver is on the way to you.',
    completed: 'Your order is complete. Thank you!',
    delivered: 'Your order has been delivered. Enjoy your meal!',
    cancelled: 'Your order has been cancelled.',
  },
};

const ConfirmationPage: React.FC = () => {
  const { tableNumber, orderNumber } = useParams<{ tableNumber: string; orderNumber: string }>();
  const location = useLocation();
  const navigate = useNavigate();
  const { mode } = useOrder();
  const { lang, setLang, t } = useLanguage();
  const langKey = lang === 'de' ? 'de' : 'en';
  const { brand } = useBrand();
  // Don Vito primary is cream (logo-faithful) — use the amber accent for UI pops.
  const accent = brand.id === 'donvito' ? brand.accent : brand.primary;
  const accentRgb = brand.id === 'donvito' ? '232,169,75' : '148,232,21';

  const [order, setOrder] = useState<OrderData | null>(null);
  const [orderId, setOrderId] = useState<string>(() => {
    const fromState = (location.state as any)?.orderId;
    if (fromState) return fromState;
    // Fallback: check localStorage (persists across refresh)
    try {
      if (orderNumber) {
        const stored = localStorage.getItem(`order_${orderNumber}`);
        if (stored) return stored;
      }
    } catch {}
    return '';
  });
  const [prevStatus, setPrevStatus] = useState<string>('');
  const [showInvoice, setShowInvoice] = useState(false);
  const [downloadingPdf, setDownloadingPdf] = useState(false);

  const handleDownloadInvoicePdf = useCallback(async () => {
    if (!order) return;
    const orderNumber = (order as any).order_number || orderId;
    if (!orderNumber) return;
    setDownloadingPdf(true);
    try {
      const res: any = await getOrderInvoicePdf({
        order_number: orderNumber,
        customer_email: (order as any).customer_email || undefined,
      });
      const b64 = res?.data?.base64;
      if (!b64) throw new Error('no pdf');
      const bytes = Uint8Array.from(atob(b64), (c) => c.charCodeAt(0));
      const blob = new Blob([bytes], { type: 'application/pdf' });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = res?.data?.filename || `SushiKi-Rechnung-${orderNumber}.pdf`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);
    } catch (e) {
      alert(langKey === 'de'
        ? 'Rechnung noch nicht verfügbar. Bitte gleich erneut versuchen.'
        : 'Invoice not available yet. Please try again shortly.');
    } finally {
      setDownloadingPdf(false);
    }
  }, [order, orderId]); // eslint-disable-line react-hooks/exhaustive-deps
  const audioRef = useRef<HTMLAudioElement | null>(null);

  const isDineIn = mode === 'dine_in' && tableNumber;

  // Play notification sound
  const playSound = useCallback(() => {
    try {
      // Use Web Audio API for a pleasant notification chime
      const ctx = new (window.AudioContext || (window as any).webkitAudioContext)();
      const osc = ctx.createOscillator();
      const gain = ctx.createGain();
      osc.connect(gain);
      gain.connect(ctx.destination);
      osc.frequency.setValueAtTime(880, ctx.currentTime);
      osc.frequency.setValueAtTime(1100, ctx.currentTime + 0.1);
      osc.frequency.setValueAtTime(880, ctx.currentTime + 0.2);
      gain.gain.setValueAtTime(0.3, ctx.currentTime);
      gain.gain.exponentialRampToValueAtTime(0.01, ctx.currentTime + 0.5);
      osc.start(ctx.currentTime);
      osc.stop(ctx.currentTime + 0.5);
    } catch { /* Audio not available */ }
  }, []);

  // Lookup order by order_number if no orderId passed via state
  // Note: requires authenticated user (list permission) — will fail silently for guests
  useEffect(() => {
    if (orderId || !orderNumber) return;
    const lookupOrder = async () => {
      try {
        const q = query(collection(db, 'orders'), where('order_number', '==', orderNumber));
        const snap = await getDocs(q);
        if (!snap.empty) {
          const foundId = snap.docs[0].id;
          setOrderId(foundId);
          try { localStorage.setItem(`order_${orderNumber}`, foundId); } catch {}
        }
      } catch (e) {
        console.log('[ORDER] Lookup by order_number not permitted (guest user)');
      }
    };
    lookupOrder();
  }, [orderNumber, orderId]);

  // Real-time listener for order status — subscribes once when orderId is set
  useEffect(() => {
    if (!orderId) return;
    console.log('[ORDER] Subscribing to order:', orderId);
    const unsub = onSnapshot(
      doc(db, 'orders', orderId),
      (snap) => {
        if (!snap.exists()) {
          console.log('[ORDER] Order document does not exist');
          return;
        }
        const data = snap.data() as OrderData;
        console.log('[ORDER] Status update:', data.status);
        setOrder(data);

        setPrevStatus((prev) => {
          if (prev && data.status !== prev) {
            playSound();
          }
          return data.status;
        });
      },
      (error) => {
        console.error('[ORDER] Listener error:', error.message);
      }
    );
    return unsub;
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [orderId]);

  const currentStatus = order?.status || 'confirmed';
  const isDeliveryOrder = order?.order_type === 'delivery';
  const STATUS_STEPS = isDeliveryOrder ? STATUS_STEPS_DELIVERY : STATUS_STEPS_DEFAULT;
  const currentStepIndex = getStepIndex(currentStatus, STATUS_STEPS);
  const isCancelled = currentStatus === 'cancelled';
  const isCompleted = currentStatus === 'completed' || currentStatus === 'delivered';

  const formatPrice = (price: number) =>
    new Intl.NumberFormat('de-DE', { style: 'currency', currency: 'EUR' }).format(price);

  const formatDate = (ts: any) => {
    if (!ts) return '';
    const d = ts.toDate ? ts.toDate() : new Date(ts);
    return d.toLocaleDateString('de-DE') + ' ' + d.toLocaleTimeString('de-DE', { hour: '2-digit', minute: '2-digit' });
  };

  return (
    <div className="page confirmation-page">
      <div className="lang-toggle" style={{ position: 'fixed' }}>
        <button className={lang === 'de' ? 'active' : ''} onClick={() => setLang('de')}>DE</button>
        <button className={lang === 'en' ? 'active' : ''} onClick={() => setLang('en')}>EN</button>
      </div>

      <div className="confirmation-card" style={{ maxWidth: 520, width: '100%' }}>

        {/* ─── DINE-IN QR TABLE: Simple clean confirmation ─── */}
        {isDineIn ? (
          <>
            <div style={{
              width: 70, height: 70, borderRadius: '50%',
              background: accent, color: '#0a0807',
              display: 'flex', alignItems: 'center', justifyContent: 'center',
              fontSize: '2rem', fontWeight: 800, margin: '0 auto',
              boxShadow: `0 8px 24px rgba(${accentRgb},0.35)`,
            }}>&#10003;</div>

            <h1 style={{ fontSize: '1.4rem', margin: '16px 0 6px', color: '#fff' }}>
              {langKey === 'de' ? 'Vielen Dank!' : 'Thank you!'}
            </h1>
            <p style={{ fontSize: '1rem', color: accent, fontWeight: 600, margin: '0 0 4px' }}>
              {langKey === 'de' ? 'Bestellung' : 'Order'} #{orderNumber || order?.order_number}
            </p>
            <p style={{ fontSize: '0.95rem', color: '#bbb', margin: '0 0 20px', lineHeight: 1.5 }}>
              {langKey === 'de'
                ? 'Ihre Bestellung wurde an die Küche gesendet. Bitte warten Sie an Ihrem Tisch.'
                : 'Your order has been sent to the kitchen. Please wait at your table.'}
            </p>

            {/* Table info */}
            <div style={{
              background: `rgba(${accentRgb},0.1)`, border: `1px solid rgba(${accentRgb},0.25)`,
              borderRadius: 12, padding: '14px 18px', marginBottom: 16, textAlign: 'center',
            }}>
              <div style={{ fontSize: '0.8rem', color: '#9ca3af', marginBottom: 4 }}>
                {langKey === 'de' ? 'Tisch' : 'Table'}
              </div>
              <div style={{ fontSize: '1.8rem', fontWeight: 800, color: accent }}>
                {tableNumber}
              </div>
            </div>

            {/* Order summary */}
            {order && (
              <div style={{
                background: 'rgba(255,255,255,0.04)', borderRadius: 10,
                padding: '12px 16px', marginBottom: 16, width: '100%',
              }}>
                {order.items?.map((item, i) => (
                  <div key={i} style={{
                    display: 'flex', justifyContent: 'space-between', alignItems: 'center',
                    padding: '6px 0', borderBottom: i < order.items.length - 1 ? '1px solid rgba(255,255,255,0.06)' : 'none',
                  }}>
                    <span style={{ color: '#e0e0e0', fontSize: '0.85rem' }}>
                      {item.quantity}x {langKey === 'de' ? (item.name_de || item.name) : item.name}
                    </span>
                    <span style={{ color: accent, fontWeight: 600, fontSize: '0.85rem' }}>
                      {formatPrice(item.total || item.price * item.quantity)}
                    </span>
                  </div>
                ))}
                <div style={{
                  display: 'flex', justifyContent: 'space-between', alignItems: 'center',
                  padding: '10px 0 0', marginTop: 6, borderTop: `1px solid rgba(${accentRgb},0.2)`,
                }}>
                  <span style={{ color: '#fff', fontWeight: 700, fontSize: '1rem' }}>
                    {langKey === 'de' ? 'Gesamt' : 'Total'}
                  </span>
                  <span style={{ color: accent, fontWeight: 700, fontSize: '1.1rem' }}>
                    {formatPrice(order.total)}
                  </span>
                </div>
              </div>
            )}

            <button className="btn-primary" style={{ width: '100%', marginTop: 8 }}
              onClick={() => navigate(`/table/${tableNumber}`)}>
              {langKey === 'de' ? 'Weitere Artikel bestellen' : 'Order more items'}
            </button>
          </>
        ) : (
        /* ─── DELIVERY / PICKUP: Full progress tracker ─── */
        <>
        {/* Status Icon */}
        {isCancelled ? (
          <div className="check-icon" style={{ background: '#ef5350' }}>&#10007;</div>
        ) : isCompleted ? (
          <div className="check-icon">&#10003;</div>
        ) : (
          <div className="check-icon" style={{ background: '#ff9800', animation: 'pulse 2s infinite' }}>
            <span style={{ fontSize: '1.2em' }}>&#9679;</span>
          </div>
        )}

        <h1 style={{ fontSize: '1.3rem', margin: '12px 0 4px' }}>
          {langKey === 'de' ? 'Bestellung' : 'Order'} #{orderNumber || order?.order_number}
        </h1>
        <p className="conf-subtitle" style={{ marginBottom: 20, fontSize: '0.9rem' }}>
          {STATUS_MESSAGES[langKey][currentStatus] || currentStatus}
        </p>

        {/* Progress Bar */}
        {!isCancelled && (
          <div className="order-progress">
            {STATUS_STEPS.map((step, i) => {
              const isActive = i <= currentStepIndex;
              const isCurrent = i === currentStepIndex;
              return (
                <div key={step} className="progress-step">
                  <div className={`progress-dot ${isActive ? 'active' : ''} ${isCurrent ? 'current' : ''}`}>
                    {isActive ? '\u2713' : i + 1}
                  </div>
                  <span className={`progress-label ${isActive ? 'active' : ''}`}>
                    {STATUS_LABELS[langKey][step]}
                  </span>
                  {i < STATUS_STEPS.length - 1 && (
                    <div className={`progress-line ${i < currentStepIndex ? 'active' : ''}`} />
                  )}
                </div>
              );
            })}
          </div>
        )}

        {/* DELIVERY PIN — Fraud prevention #1
            Customer must give this PIN to the driver before they can mark order as delivered.
            Hidden once order is delivered (PIN no longer needed). */}
        {order?.order_type === 'delivery' && order.delivery_pin && !order.delivery_pin_verified
            && order.status !== 'delivered' && order.status !== 'completed' && order.status !== 'cancelled' && (
          <div style={{
            background: 'linear-gradient(135deg, rgba(148,232,21,0.12), rgba(76,175,80,0.08))',
            border: '2px solid rgba(148,232,21,0.4)',
            borderRadius: 14, padding: '16px 18px', margin: '16px 0',
            textAlign: 'center',
          }}>
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 6, marginBottom: 6 }}>
              <span style={{ fontSize: '1.3rem' }}>🔐</span>
              <span style={{ color: '#94e815', fontWeight: 700, fontSize: '0.85rem', textTransform: 'uppercase', letterSpacing: '0.05em' }}>
                {langKey === 'de' ? 'Ihr Liefer-PIN' : 'Your Delivery PIN'}
              </span>
            </div>
            <div style={{
              fontSize: '2.6rem', fontWeight: 800, color: '#94e815', letterSpacing: '0.5rem',
              fontFamily: 'monospace', margin: '4px 0 8px',
            }}>
              {order.delivery_pin}
            </div>
            <div style={{ color: '#9ca3af', fontSize: '0.8rem', lineHeight: 1.4 }}>
              {langKey === 'de'
                ? 'Bitte teilen Sie diesen PIN dem Fahrer mit, wenn er Ihre Bestellung liefert.'
                : 'Please share this PIN with the driver when they deliver your order.'}
            </div>
          </div>
        )}

        {/* Order Details */}
        {order && (
          <div className="order-details-section">
            <div className="order-meta">
              {order.order_type === 'delivery' && order.delivery_address && (
                <div className="meta-row">
                  <span className="meta-label">{langKey === 'de' ? 'Lieferadresse' : 'Delivery to'}</span>
                  <span>{order.delivery_address}</span>
                </div>
              )}
              <div className="meta-row">
                <span className="meta-label">{langKey === 'de' ? 'Zahlung' : 'Payment'}</span>
                <span>{order.payment_method === 'cash' ? (langKey === 'de' ? 'Bar' : 'Cash') : 'PayPal'}</span>
              </div>
              <div className="meta-row">
                <span className="meta-label">{langKey === 'de' ? 'Gesamt' : 'Total'}</span>
                <span style={{ fontWeight: 700, color: '#94e815', fontSize: '1.1rem' }}>{formatPrice(order.total)}</span>
              </div>
            </div>
          </div>
        )}

        {/* Rider info for delivery orders */}
        {order && isDeliveryOrder && (order as any).rider_name && (
          <div style={{
            margin: '12px 0', padding: '10px 14px',
            background: 'rgba(41,182,246,0.08)', border: '1px solid rgba(41,182,246,0.2)',
            borderRadius: 8, display: 'flex', alignItems: 'center', gap: 10
          }}>
            <div style={{
              width: 36, height: 36, borderRadius: '50%', background: '#29B6F6',
              display: 'flex', alignItems: 'center', justifyContent: 'center',
              color: '#fff', fontWeight: 700, fontSize: 14
            }}>
              {((order as any).rider_name || '?')[0].toUpperCase()}
            </div>
            <div>
              <div style={{ fontWeight: 600, color: '#e0e0e0', fontSize: '0.9rem' }}>
                {(order as any).rider_name}
              </div>
              <div style={{ fontSize: '0.75rem', color: '#29B6F6' }}>
                {langKey === 'de' ? 'Ihr Fahrer' : 'Your Driver'}
              </div>
            </div>
          </div>
        )}

        {/* Invoice Button — show after completed */}
        {isCompleted && order && (
          <button
            className="btn-secondary"
            style={{ width: '100%', marginTop: 12, border: '1px solid rgba(148,232,21,0.3)', color: '#94e815' }}
            onClick={() => setShowInvoice(!showInvoice)}
          >
            {showInvoice
              ? (langKey === 'de' ? 'Rechnung ausblenden' : 'Hide Invoice')
              : (langKey === 'de' ? 'Rechnung anzeigen' : 'View Invoice')}
          </button>
        )}

        {/* Download the server-generated PDF (correct 7%/19% VAT, brand-aware) */}
        {isCompleted && order && (
          <button
            className="btn-secondary"
            style={{ width: '100%', marginTop: 8, border: '1px solid rgba(148,232,21,0.3)', color: '#94e815' }}
            disabled={downloadingPdf}
            onClick={handleDownloadInvoicePdf}
          >
            {downloadingPdf
              ? (langKey === 'de' ? 'Wird geladen…' : 'Loading…')
              : (langKey === 'de' ? 'Rechnung als PDF herunterladen' : 'Download Invoice PDF')}
          </button>
        )}

        {/* Invoice — header swaps to Don Vito branding when the order is from the
            Don Vito brand OR contains any pizza line items. Physical printer branding
            is unaffected (it stays on SushiKi per client). */}
        {showInvoice && order && (() => {
          const hasPizza = (order.items || []).some((it: any) => {
            const c = (it.category_name || it.category || '').toString().toLowerCase();
            return c === 'pizza' || c.startsWith('pizza');
          });
          const isDonVito = brand.id === 'donvito' || hasPizza;
          const logoSrc = isDonVito ? '/Pizza_Don_Vito_hd.png' : '/sushiki_logo_bk.png';
          const logoAlt = isDonVito ? 'Pizza Don Vito' : 'SushiKi';
          const companyName = isDonVito ? 'PIZZA DON VITO UG' : 'SushiKi UG';
          return (
          <div className="invoice-section">
            <div className="invoice-header">
              <img src={logoSrc} alt={logoAlt} style={{ height: 120, marginBottom: 8 }}
                onError={(e) => { (e.target as HTMLImageElement).style.display = 'none'; }} />
              <strong>{companyName}</strong>
              <span>Sudetenstr. 60, 50354 Hurth</span>
              <span>Tel: 02233 7063324</span>
              <span>USt-IdNr: DE123456789</span>
            </div>
            <div className="invoice-title">
              {langKey === 'de' ? 'RECHNUNG' : 'INVOICE'} #{order.order_number}
            </div>
            <div className="invoice-meta">
              <span>{langKey === 'de' ? 'Datum' : 'Date'}: {formatDate(order.created_at)}</span>
              <span>{langKey === 'de' ? 'Kunde' : 'Customer'}: {order.customer_name}</span>
            </div>
            <table className="invoice-table">
              <thead>
                <tr>
                  <th style={{ textAlign: 'left' }}>{langKey === 'de' ? 'Artikel' : 'Item'}</th>
                  <th>{langKey === 'de' ? 'Anz.' : 'Qty'}</th>
                  <th style={{ textAlign: 'right' }}>{langKey === 'de' ? 'Preis' : 'Price'}</th>
                </tr>
              </thead>
              <tbody>
                {order.items.map((item, i) => (
                  <tr key={i}>
                    <td style={{ textAlign: 'left' }}>
                      {langKey === 'de' ? (item.name_de || item.name) : item.name}
                      {item.addons && item.addons.length > 0 && (
                        <div style={{ fontSize: '0.75rem', color: '#9ca3af' }}>
                          + {item.addons.map(a => a.name).join(', ')}
                        </div>
                      )}
                      {item.notes && <div style={{ fontSize: '0.75rem', color: '#9ca3af', fontStyle: 'italic' }}>{item.notes}</div>}
                    </td>
                    <td>{item.quantity}</td>
                    <td style={{ textAlign: 'right' }}>{formatPrice(item.total || item.price * item.quantity)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
            <div className="invoice-totals">
              {(() => {
                // Real German multi-rate VAT (7% takeaway/delivery food + 19%
                // dine-in/drinks) from the server snapshot. Falls back to the
                // legacy flat calc only for old orders without a breakdown.
                const byRate = order.tax_breakdown?.by_rate;
                if (byRate && Object.keys(byRate).length > 0) {
                  const net = Object.values(byRate).reduce((s: number, b: any) => s + (b.net || 0), 0);
                  const rows = Object.keys(byRate)
                    .sort((a, b) => Number(a) - Number(b))
                    .map((rate) => (
                      <div className="invoice-row" key={rate}>
                        <span>{langKey === 'de' ? `MwSt. ${rate}%` : `VAT ${rate}%`}</span>
                        <span>{formatPrice(byRate[rate].tax || 0)}</span>
                      </div>
                    ));
                  return (
                    <>
                      <div className="invoice-row"><span>{langKey === 'de' ? 'Zwischensumme netto' : 'Subtotal (net)'}</span><span>{formatPrice(net)}</span></div>
                      {rows}
                    </>
                  );
                }
                return (
                  <>
                    <div className="invoice-row"><span>{langKey === 'de' ? 'Zwischensumme' : 'Subtotal'}</span><span>{formatPrice(order.subtotal || order.total / 1.19)}</span></div>
                    <div className="invoice-row"><span>{langKey === 'de' ? 'MwSt. 19%' : 'VAT 19%'}</span><span>{formatPrice(order.tax || order.total - order.total / 1.19)}</span></div>
                  </>
                );
              })()}
              {order.delivery_fee && order.delivery_fee > 0 ? (
                <div className="invoice-row"><span>{langKey === 'de' ? 'Lieferkosten' : 'Delivery Fee'}</span><span>{formatPrice(order.delivery_fee)}</span></div>
              ) : null}
              {order.discount_amount && order.discount_amount > 0 ? (
                <>
                  <div className="invoice-row" style={{ fontWeight: 600 }}>
                    <span>{langKey === 'de' ? 'Zwischensumme vor Rabatt' : 'Before Discount'}</span>
                    <span>{formatPrice(order.total_before_discount || (order.total + order.discount_amount))}</span>
                  </div>
                  <div className="invoice-row" style={{ color: '#16a34a', fontWeight: 700 }}>
                    <span>
                      {langKey === 'de' ? 'Rabatt' : 'Discount'}
                      {order.discount_reason ? ` (${order.discount_reason})` : (order.discount_type === 'percentage' && order.discount_value ? ` (${order.discount_value}%)` : '')}
                    </span>
                    <span>−{formatPrice(order.discount_amount)}</span>
                  </div>
                </>
              ) : null}
              <div className="invoice-row total"><span>{langKey === 'de' ? 'Gesamt' : 'Total'}</span><span>{formatPrice(order.total)}</span></div>
            </div>
            <div className="invoice-footer">
              <span>{langKey === 'de' ? 'Vielen Dank fur Ihre Bestellung!' : 'Thank you for your order!'}</span>
              <span>{isDonVito ? 'www.donvito-huerth.de' : 'www.sushiki-huerth.de'}</span>
            </div>
          </div>
          );
        })()}

        <button className="btn-primary" style={{ width: '100%', marginTop: 16 }}
          onClick={() => navigate('/')}>
          {t('newOrder')}
        </button>
        </>
        )}
      </div>
    </div>
  );
};

export default ConfirmationPage;
